import { showToast } from '@/components/toast-provider';

export interface AppError extends Error {
  code?: string;
  statusCode?: number;
  details?: any;
}

export class WebAuthnError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: Error
  ) {
    super(message);
    this.name = 'WebAuthnError';
  }
}

export class APIError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public response?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

export function handleWebAuthnError(error: any): WebAuthnError {
  console.error('WebAuthn error:', error);

  if (error.name === 'NotSupportedError') {
    return new WebAuthnError(
      'WebAuthn is not supported on this device or browser. Please try using a different browser or device.',
      'NOT_SUPPORTED',
      error
    );
  }

  if (error.name === 'SecurityError') {
    return new WebAuthnError(
      'Security error occurred. Please ensure you are on a secure connection (HTTPS) and try again.',
      'SECURITY_ERROR',
      error
    );
  }

  if (error.name === 'NotAllowedError') {
    return new WebAuthnError(
      'Authentication was cancelled or timed out. Please try again.',
      'NOT_ALLOWED',
      error
    );
  }

  if (error.name === 'InvalidStateError') {
    return new WebAuthnError(
      'This authenticator is already registered or no credentials are available.',
      'INVALID_STATE',
      error
    );
  }

  if (error.name === 'ConstraintError') {
    return new WebAuthnError(
      'The authenticator does not support the requested operation.',
      'CONSTRAINT_ERROR',
      error
    );
  }

  if (error.name === 'UnknownError') {
    return new WebAuthnError(
      'An unknown error occurred during authentication. Please try again.',
      'UNKNOWN_ERROR',
      error
    );
  }

  return new WebAuthnError(
    error.message || 'An unexpected error occurred during authentication.',
    'GENERIC_ERROR',
    error
  );
}

export function handleAPIError(response: Response, data?: any): APIError {
  const message = data?.error || data?.message || `HTTP ${response.status}: ${response.statusText}`;
  
  switch (response.status) {
    case 400:
      return new APIError('Bad request. Please check your input and try again.', 400, data);
    case 401:
      return new APIError('Authentication required. Please log in and try again.', 401, data);
    case 403:
      return new APIError('Access denied. You do not have permission to perform this action.', 403, data);
    case 404:
      return new APIError('The requested resource was not found.', 404, data);
    case 409:
      return new APIError('Conflict. The resource already exists or there is a conflict.', 409, data);
    case 429:
      return new APIError('Too many requests. Please wait a moment and try again.', 429, data);
    case 500:
      return new APIError('Internal server error. Please try again later.', 500, data);
    case 502:
      return new APIError('Bad gateway. The server is temporarily unavailable.', 502, data);
    case 503:
      return new APIError('Service unavailable. Please try again later.', 503, data);
    default:
      return new APIError(message, response.status, data);
  }
}

export function showErrorToast(error: Error | AppError | WebAuthnError | APIError) {
  console.error('Showing error toast:', error);

  if (error instanceof WebAuthnError) {
    showToast.error('Authentication Error', error.message);
  } else if (error instanceof APIError) {
    showToast.error('Server Error', error.message);
  } else {
    showToast.error('Error', error.message || 'An unexpected error occurred');
  }
}

export function showSuccessToast(message: string, description?: string) {
  showToast.success(message, description);
}

export function showInfoToast(message: string, description?: string) {
  showToast.info(message, description);
}

// Global error handler for unhandled promise rejections
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    showErrorToast(new Error('An unexpected error occurred. Please try again.'));
  });

  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    showErrorToast(new Error('An unexpected error occurred. Please try again.'));
  });
}